import * as React from 'react';

import type { CampaignAbandonMotifDto, CampaignDetailDto } from '@/api/campaign-client';
import { useAbandonCampaign, useCloseCampaign } from '@/api/use-campaigns';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/ui/modal';
import { abandonMotifLabel, ABANDON_MOTIFS } from '@/lib/campaign-labels';
import { SideCard } from '@/routes/admin/campaigns/detail-ui';

// =============================================================================
// danger-zone-panel.tsx — Carte rouge des actions IRRÉVERSIBLES du détail campagne.
// =============================================================================
// Extrait de campaign-detail-screen.tsx, gardes d'état et mutations INCHANGÉES :
//   - CLÔTURE DÉFINITIVE (A-16, UC-A-052, ARBITRAGE 1 S17) : offerte en AnalyseC1 / AnalyseC2 / Negociation
//     (DDD §3.5.5, CMP-22). Unique déclencheur du calcul consolidé ; fenêtre de confirmation forte.
//   - ABANDON (A-12, UC-A-053 ; LOT-ABANDON item 27 : Brouillon / OuverteC1 / OuverteC2) : motif (4) +
//     commentaire facultatif + email PRÉ-RÉDIGÉ MODIFIABLE (item 30), puis confirmation forte.
// GATE_HUMAN 2026-09-16 (Q4) : le formulaire d'abandon s'ouvre dans une FENÊTRE depuis cette zone rouge.
// Les deux ensembles d'états sont disjoints : la carte porte l'une OU l'autre action.

export function canCloseFinally(state: CampaignDetailDto['state']): boolean {
  return state === 'AnalyseC1' || state === 'AnalyseC2' || state === 'Negociation';
}

export function canAbandon(state: CampaignDetailDto['state']): boolean {
  return state === 'Brouillon' || state === 'OuverteC1' || state === 'OuverteC2';
}

const DESTRUCTIVE_OUTLINE =
  'border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive';

export function DangerZonePanel({
  campaign,
  onFinallyClosed,
}: {
  readonly campaign: CampaignDetailDto;
  /**
   * E13 (relevé Gate Human 2026-09-15) : au SUCCÈS de la clôture définitive, la campagne change d'état et
   * l'action peut disparaître. On remonte l'événement au parent, qui affiche une bannière PERSISTANTE.
   */
  readonly onFinallyClosed: () => void;
}): React.JSX.Element | null {
  const closable = canCloseFinally(campaign.state);
  const abandonable = canAbandon(campaign.state);
  if (!closable && !abandonable) {
    return null;
  }
  return (
    <SideCard
      headingId="detail-danger-heading"
      label={closable ? 'Clôture définitive' : 'Abandon'}
      tone="danger"
    >
      {closable ? (
        <FinalCloseAction campaign={campaign} onClosed={onFinallyClosed} />
      ) : null}
      {abandonable ? <AbandonAction campaign={campaign} /> : null}
    </SideCard>
  );
}

function FinalCloseAction({
  campaign,
  onClosed,
}: {
  readonly campaign: CampaignDetailDto;
  readonly onClosed: () => void;
}): React.JSX.Element {
  const close = useCloseCampaign();
  const [confirming, setConfirming] = React.useState(false);

  const onCloseFinally = React.useCallback((): void => {
    close.mutate(campaign.campaignId, {
      onSuccess: () => {
        setConfirming(false);
        onClosed();
      },
    });
  }, [close, campaign.campaignId, onClosed]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Déclenche le calcul consolidé (benchmark), unique déclencheur de ce calcul. Irréversible : la
        consolidation ne peut être ni annulée ni relancée.
      </p>
      <Button
        type="button"
        variant="outline"
        className={DESTRUCTIVE_OUTLINE}
        onClick={() => setConfirming(true)}
        disabled={close.isPending}
      >
        {close.isPending ? 'Clôture…' : 'Clôturer définitivement…'}
      </Button>
      {close.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {close.error.message}
        </p>
      ) : null}
      <ConfirmDialog
        open={confirming}
        title="Clôturer définitivement la campagne ?"
        description={`La campagne « ${campaign.intitule} » sera clôturée définitivement et le calcul consolidé des résultats sera déclenché. Cette action est terminale et irréversible : la consolidation ne pourra être ni annulée ni relancée. Confirmez-vous la clôture définitive ?`}
        confirmLabel={close.isPending ? 'Clôture…' : 'Confirmer la clôture définitive'}
        confirmDisabled={close.isPending}
        onConfirm={onCloseFinally}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

/**
 * Contenu PRÉ-RÉDIGÉ (défaut) de l'email de notification d'abandon aux fournisseurs participants
 * (LOT-ABANDON item 30). MODIFIABLE par l'admin avant confirmation. Contenu ÉDITORIAL COMMUN, non
 * nominatif. ANTI-LLMISM (invariant n°6) : pas d'em-dash en substitut parenthétique.
 */
const DEFAULT_ABANDON_EMAIL_BODY = [
  'Bonjour,',
  '',
  "Nous vous informons que la campagne de referencement JEKO a laquelle votre",
  "etablissement participe est arretee. Aucune action de votre part n'est requise.",
  '',
  'Nous restons a votre disposition pour toute question.',
  '',
  "L'equipe JEKO Referencement",
].join('\n');

const FIELD_CLASS =
  'w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/** Étape du parcours d'abandon : fermé → fenêtre de saisie → confirmation forte. */
type AbandonStep = 'closed' | 'form' | 'confirm';

function AbandonAction({ campaign }: { readonly campaign: CampaignDetailDto }): React.JSX.Element {
  const abandon = useAbandonCampaign();
  const [motif, setMotif] = React.useState<CampaignAbandonMotifDto>('AnnulationClient');
  const [freeText, setFreeText] = React.useState('');
  // Contenu email PRÉ-RÉDIGÉ MODIFIABLE (item 30) : initialisé au gabarit par défaut, éditable.
  const [emailBody, setEmailBody] = React.useState(DEFAULT_ABANDON_EMAIL_BODY);
  const [step, setStep] = React.useState<AbandonStep>('closed');

  // Stable : la fenêtre (piège de focus) relance son effet si `onClose` change.
  const closeForm = React.useCallback((): void => setStep('closed'), []);

  const onConfirmAbandon = React.useCallback((): void => {
    const trimmed = freeText.trim();
    const emailTrimmed = emailBody.trim();
    abandon.mutate(
      {
        campaignId: campaign.campaignId,
        body: {
          reason: {
            motif,
            // freeText OPTIONNEL : absent si vide (ne pas transmettre une chaîne vide).
            ...(trimmed.length > 0 ? { freeText: trimmed } : {}),
          },
          // emailBody OPTIONNEL : absent si vide (aucune notification alors — worker no-op tolérant).
          ...(emailTrimmed.length > 0 ? { emailBody: emailTrimmed } : {}),
        },
      },
      { onSuccess: () => setStep('closed') },
    );
  }, [abandon, campaign.campaignId, motif, freeText, emailBody]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Met fin définitivement à la campagne : elle passe à l&apos;état « Abandonnée » et ne peut plus
        être rouverte, clôturée ni modifiée. Irréversible.
      </p>
      <Button
        type="button"
        variant="outline"
        className={DESTRUCTIVE_OUTLINE}
        onClick={() => setStep('form')}
        disabled={abandon.isPending}
      >
        Abandonner la campagne…
      </Button>
      {abandon.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {abandon.error.message}
        </p>
      ) : null}

      <Modal
        open={step === 'form'}
        onClose={closeForm}
        title="Abandonner la campagne"
        description={`« ${campaign.intitule} » : choisissez le motif et relisez l'email envoyé aux fournisseurs participants.`}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setStep('confirm');
          }}
          className="space-y-4"
          aria-label="Abandonner la campagne"
        >
          <div className="space-y-2">
            <Label htmlFor="abandon-motif">Motif d&apos;abandon</Label>
            <select
              id="abandon-motif"
              name="motif"
              value={motif}
              onChange={(e) => setMotif(e.target.value as CampaignAbandonMotifDto)}
              className={`h-9 ${FIELD_CLASS}`}
            >
              {ABANDON_MOTIFS.map((value) => (
                <option key={value} value={value}>
                  {abandonMotifLabel(value)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="abandon-freetext">Commentaire (facultatif)</Label>
            <textarea
              id="abandon-freetext"
              name="freeText"
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              rows={3}
              className={`py-2 ${FIELD_CLASS}`}
            />
          </div>
          {/* LOT-ABANDON item 30 (DBU §2.5 l.1241) : email aux fournisseurs participants, contenu
              PRÉ-RÉDIGÉ MODIFIABLE avant envoi. Vider le champ = aucune notification. */}
          <div className="space-y-2">
            <Label htmlFor="abandon-email-body">
              Email aux fournisseurs participants (modifiable)
            </Label>
            <p id="abandon-email-help" className="text-xs text-muted-foreground">
              Ce message pré-rédigé est envoyé aux fournisseurs participants à la confirmation de
              l&apos;abandon. Vous pouvez le modifier ; le laisser vide n&apos;envoie aucune notification.
            </p>
            <textarea
              id="abandon-email-body"
              name="emailBody"
              aria-describedby="abandon-email-help"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              rows={8}
              className={`py-2 ${FIELD_CLASS}`}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={closeForm}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="outline"
              className={DESTRUCTIVE_OUTLINE}
              disabled={abandon.isPending}
            >
              Abandonner la campagne
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={step === 'confirm'}
        title="Confirmer l'abandon irréversible ?"
        description={`La campagne « ${campaign.intitule} » sera abandonnée pour le motif « ${abandonMotifLabel(motif)} ». Cette action est définitive et irréversible : la campagne ne pourra plus être rouverte. Confirmez-vous l'abandon ?`}
        confirmLabel={abandon.isPending ? 'Abandon…' : "Confirmer l'abandon"}
        confirmDisabled={abandon.isPending}
        onConfirm={onConfirmAbandon}
        // Annuler la confirmation ramène à la fenêtre de saisie (valeurs conservées).
        onCancel={() => setStep('form')}
      />
    </div>
  );
}
